import { useQuery } from 'react-query'
import { Car, Building2, Image, TrendingUp, Users, Eye } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { vehiclesApi, brandsApi, imagesApi } from '../lib/api'
import { formatPrice, formatNumber } from '../lib/utils'
import { Link } from 'react-router-dom'
const path = import.meta.env.VITE_IMAGE_URL
const StatCard = ({ title, value, icon: Icon, change, changeType = 'positive' }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      <Icon className="h-4 w-4 text-gray-600" />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {change && (
        <p className={`text-xs ${changeType === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
          {changeType === 'positive' ? '+' : ''}{change}
        </p>
      )}
    </CardContent>
  </Card>
)

const RecentVehicle = ({ vehicle }) => (
  <Link to={`/vehicles/${vehicle.id}/edit`} className="flex items-center space-x-4">
    <div className="flex-shrink-0">
      {vehicle.main_photo && vehicle.main_photo.length > 0 ? (
        <img
          className="h-10 w-10 rounded-lg object-cover"
          src={`${path}${vehicle.main_photo[0]}`}
          alt={vehicle.vehicle_id}
        />
      ) : (
        <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
          <Car className="h-5 w-5 text-gray-400" />
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-900 truncate">
        {vehicle.model?.model_group?.manufacturer?.name} {vehicle.model?.name}
      </p>
      <p className="text-sm text-gray-500">{vehicle.vehicle_id}</p>
    </div>
    <div className="flex-shrink-0 text-right">
      <p className="text-sm font-medium text-gray-900">{formatPrice(vehicle.price)}</p>
      <p className="text-xs text-gray-500">{vehicle.year}</p>
    </div>
  </Link>
)

export default function Dashboard() {
  const { data: vehiclesData, isLoading: vehiclesLoading } = useQuery(
    'vehicles',
    () => vehiclesApi.getAll({ limit: 5, page: 1 }),
    {
      select: (data) => data.data
    }
  )
  const { data: vehiclesStatsData } = useQuery(
    'vehicles-stats',
    () => vehiclesApi.getStats(),
    {
      select: (data) => data.data
    }
  )

  const { data: brandsData, isLoading: brandsLoading } = useQuery(
    'brands-stats',
    () => brandsApi.getAll({ limit: 1, page: 1 }),
    {
      select: (data) => data.data
    }
  )

  const { data: imagesData, isLoading: imagesLoading } = useQuery(
    'images-stats',
    () => imagesApi.getAll({ limit: 1, page: 1 }),
    {
      select: (data) => data.data.data
    }
  )

  const isLoading = vehiclesLoading || brandsLoading || imagesLoading

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Overview of your car management system
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-gray-200 rounded animate-pulse mb-2" />
                <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  const totalVehicles = vehiclesStatsData?.total_vehicle_count || 0
  const totalBrands = brandsData?.count || 0
  const totalImages = imagesData?.count || 0
  const recentVehicles = vehiclesData?.results || []
  const totalPrice = vehiclesStatsData?.total_price || 0


  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Overview of your car management system
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Vehicles"
          value={formatNumber(totalVehicles)}
          icon={Car}
          change="12% from last month"
        />
        <StatCard
          title="Total Brands"
          value={formatNumber(totalBrands)}
          icon={Building2}
          change="3% from last month"
        />
        <StatCard
          title="Total Images"
          value={formatNumber(totalImages)}
          icon={Image}
          change="8% from last month"
        />
        <StatCard
          title="Total amount in stock"
          value={formatPrice(totalPrice)}
          icon={TrendingUp}
          change={`${vehiclesStatsData?.vehicle_count} cars in total`}
        />
      </div>

      {/* Recent Vehicles */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent Vehicles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentVehicles.length > 0 ? (
                recentVehicles.map((vehicle) => (
                  <RecentVehicle key={vehicle.id} vehicle={vehicle} />
                ))
              ) : (
                <div className="text-center py-6">
                  <Car className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No vehicles</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by adding your first vehicle.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <a
                href="/vehicles/new"
                className="flex items-center p-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Car className="mr-3 h-5 w-5 text-primary-600" />
                Add New Vehicle
              </a>
              <a
                href="/brands/new"
                className="flex items-center p-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Building2 className="mr-3 h-5 w-5 text-primary-600" />
                Add New Brand
              </a>
              <a
                href="/images"
                className="flex items-center p-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Image className="mr-3 h-5 w-5 text-primary-600" />
                Manage Images
              </a>
              <a
                href="/fuel-types"
                className="flex items-center p-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <TrendingUp className="mr-3 h-5 w-5 text-primary-600" />
                Manage Filter Options
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
